import React from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { uk } from 'date-fns/locale';
import ButtonIcon from '../../../common/ButtonIcon/ButtonIcon';
import VocabularyButton from '../../../common/VocabularyButton/VocabularyButton';
import {
  getEventState,
  homeworkColors,
  homeworkPulseColors,
  HOMEWORK_DONE_COLOR,
} from '../constants';
import { rememberHomeworkEvent } from '../homework-handoff';
import MaterialTiles from '../../Programs/Programs/ProgramsCard/MaterialTiles';
import usePreviewFeature from '../../../hooks/usePreviewFeature';
import './event-modal-item.scss';
import '../../Programs/Programs/ProgramsCard/course-card.style.scss';

/** Порядок кнопок збережено з попередньої версії картки. */
const buildMaterials = (event) => [
  { key: 'presentation', icon: 'monitor', title: 'Презентація', link: event.presentation_link },
  { key: 'pdf', icon: 'pdf', title: 'PDF-конспект', link: event.google_pdf_url },
  { key: 'youtube', icon: 'youtube', title: 'Відео про курс', link: event.youtube_link },
  { key: 'vocabulary', icon: 'list', title: 'Словник уроку', link: event.vocabulary_link },
  { key: 'video', icon: 'video', title: 'Відеозапис', link: event.video_url },
].filter((material) => Boolean(material.link));

/** Див. коментар у ScheduleEvent: напрям без картинки дає бите зображення. */
const hideBrokenFlag = (e) => e.currentTarget.classList.add('is-broken');

const padHour = (hour) => `${String(hour).padStart(2, '0')}:00`;

const dayLabel = (isoDate) =>
  format(new Date(`${String(isoDate).slice(0, 10)}T00:00:00`), 'd MMMM', { locale: uk });

/**
 * Два рядки часу для студента з НЕ київським поясом: свій час і київський.
 *
 * Дату дописуємо лише коли за місцевим часом це ІНША доба (напр. 23:00 у Лос-Анджелесі —
 * це вже 09:00 наступного дня в Києві). Коли доба та сама, дата й так стоїть у заголовку
 * панелі дня, і дублювати її в кожній картці — зайвий шум.
 *
 * Київському студенту (`timezone_city` порожній) не показуємо нічого — усе як було.
 */
const buildTimeRows = (event, endHour) => {
  if (!event.timezone_city || event.kyiv_hour === null || event.kyiv_hour === undefined) {
    return null;
  }

  const shifted = Boolean(event.kyiv_date) && event.kyiv_date !== event.date;
  const suffix = (isoDate) => (shifted ? `, ${dayLabel(isoDate)}` : '');

  return {
    city: event.timezone_city,
    local: `${padHour(event.hour)} – ${padHour(endHour)}${suffix(event.date)}`,
    kyiv: `${padHour(event.kyiv_hour)}${suffix(event.kyiv_date)}`,
    // Рига, Бухарест, Стамбул улітку збігаються з Києвом — другий рядок був би дублем.
    sameAsKyiv: !shifted && String(event.kyiv_hour) === String(event.hour),
  };
};

const EventModalItem = ({ event, closeModal }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isV2 = usePreviewFeature('cabinet-v2');

  const typeKey = event.type_key || 'lesson';
  const state = getEventState(event);
  const materials = buildMaterials(event);

  // Прод-правило: пульсує все, крім виконаного ДЗ. Раніше в цій картці змінна
  // --pulse-color виставлялась, але клас pulsing-icon не додавався — пульсації не було взагалі.
  const shouldPulse = event.homework_icon_color !== HOMEWORK_DONE_COLOR;
  const pulseStyle = shouldPulse
    ? { '--pulse-color': homeworkPulseColors[event.homework_icon_color] }
    : {};

  const endHour = Number(event.hour) === 23 ? '00' : Number(event.hour) + 1;
  const timeRows = buildTimeRows(event, endHour);

  const openLink = (link) => window.open(link, '_blank');

  const openHomework = () => {
    rememberHomeworkEvent(event);
    closeModal();
    navigate('/homework', { state: { from: location.pathname } });
  };

  return (
    <div className={`event-modal-item event-modal-item--${typeKey}`}>
      <div className="event-modal-item__head">
        <span className="event-modal-item__type">{event.type_label}</span>
        <span className="event-modal-item__time">{event.hour}:00 – {endHour}:00</span>
      </div>

      <div className="event-modal-item__body">
        <div className="event-modal-item__title">
          <img
            src={event.image}
            className="event-modal-item__img"
            alt=''
            onError={hideBrokenFlag}
          />
          {event.direction_short_name}
        </div>

        {/* Уроків може бути кілька: одне відпрацювання перекриває кілька пропущених тем. */}
        {(event.lessons || []).map((lesson) => (
          <div className="event-modal-item__lesson" key={lesson.order}>
            {lesson.is_test && <span className="event-modal-item__test">Тест</span>}
            <span>
              {lesson.title ? <b>{lesson.title}</b> : null}
              {lesson.title && lesson.topic ? ' · ' : ''}
              {lesson.topic}
            </span>
          </div>
        ))}

        <div className="event-modal-item__rows">
          {/* Студенту в іншому поясі показуємо обидва часи: за його поясом і за київським,
              бо вся школа (і викладач, і менеджер) живе саме за київським. */}
          {timeRows && (
            <div className="event-modal-item__meta">
              <i>Ваш час:</i> {timeRows.local}
              <span className="event-modal-item__badge">{timeRows.city}</span>
            </div>
          )}

          {timeRows && !timeRows.sameAsKyiv && (
            <div className="event-modal-item__meta">
              <i>За Києвом:</i> {timeRows.kyiv}
            </div>
          )}

          {/* Ручне заняття програми не має — рядок просто не малюємо. */}
          {event.program_title && (
            <div className="event-modal-item__meta">
              <i>Програма:</i> {event.program_title}
            </div>
          )}

          {event.teacher_name && (
            <div className="event-modal-item__meta">
              <i>Викладач:</i> {event.teacher_name}
              {event.is_sub_teacher && <span className="event-modal-item__badge">заміна</span>}
            </div>
          )}
        </div>

        {isV2 ? (
          <div className="event-modal-item__footer event-modal-item__footer--tiles">
            <span className={`event-modal-item__state event-modal-item__state--${state.key}`}>
              {state.label}
            </span>
            <MaterialTiles lesson={event} onHomework={openHomework} />
          </div>
        ) : (
        <div className="event-modal-item__footer">
          <span className={`event-modal-item__state event-modal-item__state--${state.key}`}>
            {state.label}
          </span>

          <div className="event-modal-item__btns">
            {materials.map((material) => (
              <ButtonIcon
                key={material.key}
                icon={material.icon}
                title={material.title}
                btnBg='white'
                iconColor='green'
                btnSize='lg'
                iconSize='md'
                onClick={() => openLink(material.link)}
              />
            ))}

            <VocabularyButton token={event.vocabulary_token} />

            {event.is_show_homework && (
              <div className="event-modal-item__hw">
                {event.is_show_icon_new && <div className="new-badge">new</div>}
                <ButtonIcon
                  classes={shouldPulse ? 'event-modal-item__hw-btn' : ''}
                  icon='book'
                  title='Домашнє завдання'
                  btnBg='white'
                  iconColor={homeworkColors[event.homework_icon_color]}
                  btnSize='lg'
                  iconSize='md'
                  onClick={openHomework}
                  style={pulseStyle}
                />
              </div>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

EventModalItem.propTypes = {
  event: PropTypes.object.isRequired,
  closeModal: PropTypes.func.isRequired,
};

export default EventModalItem;
